/* ============================================================
   LEOSPORTZ — theme-product.js
   Product page: variants, gallery, tabs, quantity, quick-add, wishlist.
   ============================================================ */

(function () {
'use strict';

window.Leosportz = window.Leosportz || {};
const { $, $$, on, debounce } = Leosportz.utils;

/* ─── Color swatch hex map (fallback) ─────────────────────── */
const DEFAULT_COLOR_MAP = {
  black: '#1a1a1a', white: '#f5f5f5', red: '#c62828', blue: '#1565c0',
  green: '#2e7d32', yellow: '#f9a825', orange: '#ef6c00', purple: '#6a1b9a',
  pink: '#ad1457', grey: '#757575', gray: '#757575', navy: '#1a237e',
  teal: '#00695c', brown: '#4e342e', beige: '#d7ccc8', cream: '#fff9c4',
  maroon: '#880e4f', olive: '#558b2f', coral: '#ff7043', mint: '#69f0ae',
  lavender: '#b388ff', gold: '#ffd700', silver: '#c0c0c0', bronze: '#cd7f32',
  charcoal: '#36454f', ivory: '#fffff0', khaki: '#f0e68c', plum: '#8e4585',
  rust: '#b7410e', slate: '#708090', taupe: '#483c32', terracotta: '#e2725b',
  wine: '#722f37', indigo: '#4b0082', cobalt: '#0047ab', emerald: '#50c878',
  ruby: '#e0115f', sapphire: '#0f52ba', amber: '#ffbf00',
};

/* ─── Variant Selectors ───────────────────────────────────── */
Leosportz.ProductVariants = {
  init() {
    const form = $('[data-product-form]');
    if (!form) return;

    const variantsScript = $('script[data-product-variants]');
    const optionsScript  = $('script[data-product-options]');
    const swatchesScript = $('script[data-color-swatches]');

    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.options  = optionsScript  ? JSON.parse(optionsScript.textContent)  : [];
    this.colorMap = swatchesScript ? (JSON.parse(swatchesScript.textContent) || {}) : {};
    if (Object.keys(this.colorMap).length === 0) this.colorMap = DEFAULT_COLOR_MAP;

    this.optionEls = $$('[data-option]');
    this.inputId   = form.querySelector('input[name="id"]');
    this.priceEl   = $('#ProductPrice');
    this.compareEl = $('#ProductComparePrice');
    this.mainImg   = $('#ProductMainImage');

    this.initColorSwatches();
    this.initSizeSwatches();
    this.initBuyNow();
    this.bindEvents();
  },

  initColorSwatches() {
    $$('[data-color]').forEach(swatch => {
      const color = swatch.dataset.color;
      const hex = this.colorMap[color];
      if (hex) swatch.style.backgroundColor = hex;
      else swatch.style.backgroundColor = color;
    });
  },

  initSizeSwatches() {
    this.updateSizeAvailability();
  },

  isColorOption(name) {
    const n = (name || '').toLowerCase().replace(/[_\s]/g, '');
    return n === 'color' || n === 'colour';
  },

  isSizeOption(name) {
    const n = (name || '').toLowerCase().replace(/[_\s]/g, '');
    return n === 'size';
  },

  updateSizeAvailability() {
    const selected = this.getSelected();
    const colorIdx = this.options.findIndex(o => this.isColorOption(o.name));
    const sizeIdx  = this.options.findIndex(o => this.isSizeOption(o.name));
    if (colorIdx === -1 || sizeIdx === -1) return;

    const selectedColor = selected[colorIdx];
    const sizeOptionName = this.options[sizeIdx].name.toLowerCase().replace(/[_\s]/g, '');

    $$(`[data-option="${sizeOptionName}"]`).forEach(btn => {
      const sizeVal = btn.dataset.value;
      const hasMatch = this.variants.some(v =>
        v.available && v.options[colorIdx] === selectedColor && v.options[sizeIdx] === sizeVal
      );
      btn.classList.toggle('is-unavailable', !hasMatch);
      btn.toggleAttribute('aria-disabled', !hasMatch);
    });
  },

  getSelected() {
    const selected = [];
    this.options.forEach((opt, idx) => {
      const norm = opt.name.toLowerCase().replace(/[_\s]/g, '');
      const active = $(`[data-option="${norm}"].is-active, [data-option-index="${idx}"].is-active`);
      selected.push(active ? active.dataset.value : opt.values[0]);
    });
    return selected;
  },

  findVariant(opts) {
    return this.variants.find(v =>
      v.options.length === opts.length &&
      v.options.every((o, i) => o === opts[i])
    );
  },

  update() {
    const selected = this.getSelected();
    const variant = this.findVariant(selected);
    if (!variant) return;

    this.inputId.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = Leosportz.utils.formatMoney(variant.price);
    if (this.compareEl) {
      if (variant.compare_at_price > variant.price) {
        this.compareEl.textContent = Leosportz.utils.formatMoney(variant.compare_at_price);
        this.compareEl.style.display = '';
      } else {
        this.compareEl.style.display = 'none';
      }
    }

    // Update image
    if (variant.featured_image && this.mainImg) {
      this.mainImg.src = variant.featured_image.src;
      this.mainImg.srcset = '';
    }

    // Update ATC button
    const atcBtn = $('[data-atc-btn]');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.innerHTML = `<span class="material-symbols-outlined" style="font-size:18px">shopping_bag</span> ${variant.available ? 'Add to Cart' : 'Sold Out'}`;
    }

    // Refresh delivery estimate for new variant if pincode was already checked
    if (Leosportz.DeliveryChecker?.refreshIfActive) Leosportz.DeliveryChecker.refreshIfActive();

    // Update option value labels
    this.options.forEach((opt, idx) => {
      const label = $(`#OptionValue-${idx + 1}`);
      if (label) label.textContent = selected[idx];
    });

    this.updateSizeAvailability();
  },

  bindEvents() {
    $$('[data-option]').forEach(el => {
      on(el, 'click', () => {
        const optionName = el.dataset.option;
        $$(`[data-option="${optionName}"]`).forEach(s => s.classList.remove('is-active'));
        el.classList.add('is-active');
        this.update();
      });
    });
  },

  initBuyNow() {
    const btn = $('[data-buy-now]');
    const form = $('[data-product-form]');
    if (!btn) return;
    on(btn, 'click', async () => {
      const variantId = this.inputId?.value;
      if (!variantId) return;
      const qtyInput = form?.querySelector('.qty-selector__input');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty }),
        });
        window.location.href = '/checkout';
      } catch (err) {
        console.error('Buy Now failed:', err);
      }
    });
  },
};

/* ─── Gallery ─────────────────────────────────────────────── */
Leosportz.Gallery = {
  images: [],
  currentIndex: 0,

  init() {
    this.thumbs = $$('.product-gallery__thumb');
    this.gridItems = $$('.product-gallery__grid-item');
    this.mainImg = $('#ProductMainImage');
    this.lightbox = $('[data-lightbox]');
    this.lightboxImg = $('[data-lightbox-img]');
    this.lightboxCounter = $('[data-lightbox-counter]');

    if (!this.mainImg) return;

    // Build image array from thumbs
    this.images = this.thumbs.map(t => ({
      src: t.dataset.fullSrc || t.dataset.src,
      alt: t.querySelector('img')?.alt || '',
    }));

    this.initThumbs();
    this.initGrid();
    this.initLightbox();
    this.initKeyboard();
  },

  initThumbs() {
    this.thumbs.forEach(thumb => {
      on(thumb, 'click', () => this.showImage(parseInt(thumb.dataset.thumbIndex, 10)));
    });
  },

  initGrid() {
    this.gridItems.forEach(item => {
      on(item, 'click', () => this.showImage(parseInt(item.dataset.gridIndex, 10)));
    });
  },

  showImage(index) {
    if (index < 0 || index >= this.images.length) return;
    this.currentIndex = index;
    const img = this.images[index];

    // Update main image
    if (this.mainImg) {
      this.mainImg.src = img.src;
      this.mainImg.srcset = '';
      this.mainImg.dataset.galleryIndex = index;
    }

    // Update active thumb
    this.thumbs.forEach(t => t.classList.toggle('is-active', parseInt(t.dataset.thumbIndex, 10) === index));

    // Update active grid item
    this.gridItems.forEach(g => g.classList.toggle('is-active', parseInt(g.dataset.gridIndex, 10) === index));

    // Scroll thumb into view
    const activeThumb = this.thumbs[index];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  },

  initLightbox() {
    if (!this.lightbox) return;

    // Open on main image click
    const main = $('[data-gallery-main]');
    if (main) {
      on(main, 'click', () => this.openLightbox(this.currentIndex));
    }

    // Close button
    const closeBtn = $('[data-lightbox-close]');
    if (closeBtn) on(closeBtn, 'click', () => this.closeLightbox());

    // Prev / Next
    const prevBtn = $('[data-lightbox-prev]');
    const nextBtn = $('[data-lightbox-next]');
    if (prevBtn) on(prevBtn, 'click', (e) => { e.stopPropagation(); this.prevImage(); });
    if (nextBtn) on(nextBtn, 'click', (e) => { e.stopPropagation(); this.nextImage(); });

    // Close on backdrop click
    on(this.lightbox, 'click', (e) => {
      if (e.target === this.lightbox) {
        this.closeLightbox();
      }
    });
  },

  openLightbox(index) {
    if (!this.lightbox || !this.images.length) return;
    this.currentIndex = index;
    this.updateLightboxImage();
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  },

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  updateLightboxImage() {
    if (!this.lightboxImg || !this.images[this.currentIndex]) return;
    const img = this.images[this.currentIndex];
    this.lightboxImg.src = img.src;
    this.lightboxImg.alt = img.alt;
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }
  },

  prevImage() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.updateLightboxImage();
    this.showImage(this.currentIndex);
  },

  nextImage() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.updateLightboxImage();
    this.showImage(this.currentIndex);
  },

  initKeyboard() {
    on(document, 'keydown', (e) => {
      if (!this.lightbox || !this.lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.prevImage();
      if (e.key === 'ArrowRight') this.nextImage();
    });
  },
};

/* ─── Product Tabs ────────────────────────────────────────── */
Leosportz.ProductTabs = {
  init() {
    const tabBtns = $$('.tab-btn');
    const tabPanels = $$('.tab-panel');
    if (!tabBtns.length) return;

    tabBtns.forEach(btn => {
      on(btn, 'click', () => {
        const target = btn.dataset.tab;
        tabBtns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
        tabPanels.forEach(p => { p.hidden = true; });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        const panel = document.getElementById(target);
        if (panel) panel.hidden = false;
      });
    });
  },
};

/* ─── Quantity Selectors ──────────────────────────────────── */
Leosportz.QtySelectors = {
  init() {
    $$('.qty-selector__btn').forEach(btn => {
      on(btn, 'click', () => {
        const input = btn.parentElement.querySelector('input[type="number"]');
        if (!input) return;
        const step = parseInt(btn.dataset.step, 10);
        let val = parseInt(input.value, 10) || 1;
        val = Math.max(1, Math.min(99, val + step));
        input.value = val;
      });
    });
  },
};

/* ─── Quick Add ───────────────────────────────────────────── */
Leosportz.QuickAdd = {
  init() {
    $$('[data-quick-add]').forEach(btn => {
      on(btn, 'click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;
        await Leosportz.CartDrawer.addItem(variantId, 1);
      });
    });
  },
};

/* ─── Wishlist (localStorage) ─────────────────────────────── */
Leosportz.Wishlist = {
  KEY: 'lz_wishlist',

  init() {
    $$('[data-wishlist]').forEach(btn => {
      const id = btn.dataset.wishlist;
      this.updateBtn(btn, id);
      on(btn, 'click', () => {
        this.toggle(id);
        this.updateBtn(btn, id);
      });
    });
  },

  get() {
    try { return JSON.parse(localStorage.getItem(this.KEY)) || []; }
    catch { return []; }
  },

  toggle(id) {
    const list = this.get();
    const idx = list.indexOf(id);
    if (idx > -1) list.splice(idx, 1);
    else list.push(id);
    localStorage.setItem(this.KEY, JSON.stringify(list));
  },

  updateBtn(btn, id) {
    const active = this.get().includes(id);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active);
  },
};

/* ─── Delivery Checker (Pincode serviceability) ─────────────
   Calls fulfillment-worker /api/eta — MCF primary, Shiprocket fallback.
   Local ZONE_PREFIXES kept as last-resort if endpoint unreachable.
*/
Leosportz.DeliveryChecker = {
  PINCODE_KEY: 'lz_pincode',
  API_BASE: 'https://fulfillment.leosportz.in',

  /* Fallback only — used when /api/eta call fails */
  ZONE_PREFIXES: {
    metro: { prefixes: ['110','121','122','201'], label: 'Delhi NCR', daysMin: 1, daysMax: 2, cod: true },
    tier1: { prefixes: ['400','401','560','570','500','501','600','700','411','410'], label: 'Metro City', daysMin: 2, daysMax: 3, cod: true },
    tier2: { prefixes: ['380','390','360','362','395','396','452','462','422','226','208','302','311','492','751','641','673'], label: 'Tier 2 City', daysMin: 3, daysMax: 4, cod: true },
  },

  init() {
    this.container = $('[data-delivery-checker]');
    if (!this.container) return;

    this.input = $('[data-delivery-input]', this.container);
    this.checkBtn = $('[data-delivery-check]', this.container);
    this.resultEl = $('[data-delivery-result]', this.container);
    this.statusEl = $('[data-delivery-status]', this.container);
    this.metaEl = $('[data-delivery-meta]', this.container);
    this.defaultEl = $('[data-delivery-default]', this.container);

    // Restore saved pincode
    const saved = sessionStorage.getItem(this.PINCODE_KEY);
    if (saved) {
      this.input.value = saved;
      this.check(saved);
    }

    this.bindEvents();
  },

  bindEvents() {
    if (this.checkBtn) {
      on(this.checkBtn, 'click', () => this.check(this.input.value.trim()));
    }
    if (this.input) {
      on(this.input, 'keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.check(this.input.value.trim());
        }
      });
      on(this.input, 'input', () => {
        // Allow only digits
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      });
    }
  },

  async check(pincode) {
    if (!pincode || pincode.length !== 6) {
      this.showError('Please enter a valid 6-digit PIN code');
      return;
    }
    sessionStorage.setItem(this.PINCODE_KEY, pincode);
    this.showLoading();

    try {
      const ctx = this.currentVariantContext();
      if (!ctx) throw new Error('no variant');
      const url = new URL(this.API_BASE + '/api/eta');
      url.search = new URLSearchParams({
        pincode,
        variant_id: ctx.id,
        price: ctx.price.toFixed(2),
        weight_kg: ctx.weightKg.toFixed(2),
        qty: 1,
      }).toString();
      const r = await fetch(url, { method: 'GET' });
      if (!r.ok) throw new Error('api ' + r.status);
      const j = await r.json();
      this.showApiResult(pincode, j);
    } catch (err) {
      console.warn('[delivery-checker] API failed, fallback:', err.message);
      this.showLocalFallback(pincode);
    }
  },

  currentVariantContext() {
    // 1. Variant id: prefer Leosportz.Variants tracker (updates on option change),
    //    else fall back to the cart form's selected id input (works for single-variant
    //    products where Variants tracker is not initialized).
    const trackerId = window.Leosportz?.Variants?.inputId?.value;
    const formId = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
    const id = trackerId || formId;
    if (!id) return null;

    // 2. Variants list: prefer Variants tracker; else parse the embedded
    //    [data-product-variants] JSON that the template always emits.
    let variants = window.Leosportz?.Variants?.variants;
    if (!Array.isArray(variants) || variants.length === 0) {
      const el = document.querySelector('[data-product-variants]');
      try { variants = el ? JSON.parse(el.textContent) : []; } catch { variants = []; }
    }
    const variant = variants.find(x => String(x.id) === String(id));
    if (!variant) return null;
    return {
      id: variant.id,
      price: (variant.price || 0) / 100,
      weightKg: (variant.grams || 500) / 1000,
    };
  },

  showLoading() {
    this.resultEl.className = 'delivery-checker__result is-loading';
    this.statusEl.innerHTML = 'Checking…';
    this.metaEl.textContent = '';
    this.resultEl.style.display = '';
    this.defaultEl.style.display = 'none';
  },

  showApiResult(pincode, j) {
    const dateLabel = j.latest
      ? new Date(j.latest).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
      : `${j.days} days`;
    const sourceLabel = j.source === 'mcf' ? 'Fulfilled by Amazon' : 'Express shipping';
    this.resultEl.className = 'delivery-checker__result is-available';
    this.statusEl.innerHTML = `<span class="material-symbols-outlined">check_circle</span> Delivery available to ${pincode}`;
    this.metaEl.innerHTML = `Get it by <strong>${dateLabel}</strong> · ${sourceLabel}`;
    this.resultEl.style.display = '';
    this.defaultEl.style.display = 'none';
  },

  showLocalFallback(pincode) {
    const prefix = pincode.slice(0, 3);
    let zone = null;
    for (const key in this.ZONE_PREFIXES) {
      if (this.ZONE_PREFIXES[key].prefixes.includes(prefix)) { zone = this.ZONE_PREFIXES[key]; break; }
    }
    if (!zone) zone = { label: 'Your Location', daysMin: 3, daysMax: 5, cod: true };
    this.showSuccess(pincode, zone);
  },

  // Called externally when variant changes
  refreshIfActive() {
    const saved = sessionStorage.getItem(this.PINCODE_KEY);
    if (saved && this.input) this.check(saved);
  },

  showSuccess(pincode, zone) {
    const deliveryRange = this.formatDeliveryRange(zone.daysMin, zone.daysMax);
    const codText = zone.cod ? ' · COD available' : '';

    this.resultEl.className = 'delivery-checker__result is-available';
    this.statusEl.innerHTML = `
      <span class="material-symbols-outlined">check_circle</span>
      Delivery available to ${pincode}
    `;
    this.metaEl.innerHTML = `
      Estimated delivery: <strong>${deliveryRange}</strong>${codText}
    `;
    this.resultEl.style.display = '';
    this.defaultEl.style.display = 'none';
  },

  showError(msg) {
    this.resultEl.className = 'delivery-checker__result is-unavailable';
    this.statusEl.innerHTML = `
      <span class="material-symbols-outlined">error</span>
      ${msg}
    `;
    this.metaEl.textContent = '';
    this.resultEl.style.display = '';
    this.defaultEl.style.display = 'none';
  },

  formatDeliveryRange(min, max) {
    const today = new Date();
    const d1 = new Date(today); d1.setDate(today.getDate() + min);
    const d2 = new Date(today); d2.setDate(today.getDate() + max);

    const opts = { day: 'numeric', month: 'short' };
    const s1 = d1.toLocaleDateString('en-IN', opts);
    const s2 = d2.toLocaleDateString('en-IN', opts);

    if (s1 === s2) return s1;
    return `${s1} – ${s2}`;
  },
};


})();
